<template>
  <div class="relative category-filter-dropdown flex-shrink-0" :class="iconOnly ? 'group/category-filter' : ''">
    <button v-if="iconOnly" ref="buttonEl" type="button" @click="toggleOpen" @keydown="handleKeydown"
      class="w-7 h-7 flex items-center justify-center rounded-lg border transition-colors cursor-pointer"
      :class="selected.length
        ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
        : 'text-gray-500 dark:text-gray-400 bg-white dark:bg-[#1C2128] border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'">
      <svg class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75"/>
      </svg>
    </button>

    <!-- Tooltip: only for the icon-only variant (the full button already
         speaks for itself via its own "Category" label) and only while the
         dropdown itself isn't open, so the two floating panels never stack. -->
    <div v-if="iconOnly && !open"
      class="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 z-50 opacity-0 scale-95 group-hover/category-filter:opacity-100 group-hover/category-filter:scale-100 transition-all duration-150 origin-top">
      <div class="relative whitespace-nowrap rounded-md bg-gray-900 dark:bg-gray-700 px-2 py-1 text-[11px] font-medium text-white shadow-lg">
        Category{{ selected.length ? ` (${selected.length})` : '' }}
        <div class="absolute left-1/2 -translate-x-1/2 -top-1 w-2 h-2 rotate-45 bg-gray-900 dark:bg-gray-700"></div>
      </div>
    </div>

    <button v-if="!iconOnly" ref="buttonEl" type="button" @click="toggleOpen" @keydown="handleKeydown"
      class="h-full flex items-center gap-1.5 pl-3 pr-2.5 py-1.5 text-sm border rounded-lg transition-colors cursor-pointer"
      :class="selected.length
        ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
        : 'text-gray-600 dark:text-gray-300 bg-white dark:bg-[#1C2128] border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'">
      <svg class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75"/>
      </svg>
      <span class="whitespace-nowrap">Category{{ selected.length ? ` (${selected.length})` : '' }}</span>
      <svg class="w-3.5 h-3.5 flex-shrink-0 transition-transform" :class="open ? 'rotate-180' : ''"
        fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
      </svg>
    </button>

    <!-- Teleported + fixed for the same reason as CriticalityField's own
         dropdown: escapes this panel's scroll/overflow context. -->
    <Teleport to="body">
      <div v-if="open" class="fixed inset-0 z-[9998]" @click="open = false"></div>
      <div v-if="open" :style="panelStyle"
        class="fixed z-[9999] bg-white dark:bg-[#1C2128] border border-gray-100 dark:border-gray-700 rounded-lg shadow-lg py-1.5 max-h-80 overflow-hidden flex flex-col">
        <div class="px-3.5 pb-1.5 flex-shrink-0 flex items-center justify-between gap-2">
          <p class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
            Categories in your stack
          </p>
          <button v-if="selected.length" type="button" @click="$emit('clear')"
            class="text-[11px] font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 cursor-pointer flex-shrink-0">
            Clear
          </button>
        </div>
        <div class="overflow-y-auto">
          <button v-for="(category, index) in categories" :key="category.value" type="button"
            :ref="(el) => setOptionRef(el, index)"
            @click="$emit('toggle', category.value)" @mouseenter="activeIndex = index"
            :class="index === activeIndex ? 'bg-gray-50 dark:bg-gray-800' : ''"
            class="w-full flex items-center gap-2.5 text-left px-3.5 py-2 transition-colors cursor-pointer">
            <span class="w-4 h-4 rounded flex-shrink-0 border-2 flex items-center justify-center transition-colors"
              :class="isSelected(category.value) ? 'bg-blue-600 border-blue-600' : 'border-gray-300 dark:border-gray-600'">
              <svg v-if="isSelected(category.value)" class="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
              </svg>
            </span>
            <span class="flex-1 min-w-0 text-sm truncate" :class="isSelected(category.value) ? 'font-medium text-blue-700 dark:text-blue-400' : 'text-gray-700 dark:text-gray-200'">
              {{ category.label }}
            </span>
            <span class="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">{{ category.count }}</span>
          </button>
          <p v-if="categories.length === 0" class="px-3.5 py-4 text-xs text-gray-400 dark:text-gray-500 text-center">
            No categories yet. Add a component to your stack first.
          </p>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'

const props = defineProps({
  // [{ value, label, count }], already limited to categories actually
  // present among this org's own added components (not the full 25-value
  // TYPE_OPTIONS list) — see OrgComponentsPanel's availableCategories.
  categories: { type: Array, required: true },
  selected: { type: Array, required: true },
  // Compact square icon button (with a hover tooltip instead of a visible
  // label) rather than the full "Category (n)" pill — used where the full
  // button doesn't fit the surrounding header (GlobalCatalogPanel), while
  // OrgComponentsPanel keeps the full labeled button as-is.
  iconOnly: { type: Boolean, default: false },
})

const emit = defineEmits(['toggle', 'clear'])

const open = ref(false)
const activeIndex = ref(-1)
const optionRefs = ref([])
const buttonEl = ref(null)
const panelStyle = ref({})

function isSelected(value) {
  return props.selected.includes(value)
}

function setOptionRef(el, index) {
  if (el) optionRefs.value[index] = el
}

function positionPanel() {
  const rect = buttonEl.value.getBoundingClientRect()
  const width = Math.max(rect.width, 260)
  // Left-align under the button by default, but flip to right-aligned
  // (anchored to the button's right edge) whenever left-aligning would push
  // the panel past the viewport's right edge — otherwise a button sitting
  // near the right side of the screen (e.g. GlobalCatalogPanel's header,
  // which itself hugs the right edge) renders its dropdown partly or fully
  // off-screen. An 8px margin keeps it from touching the edge exactly.
  const overflowsRight = rect.left + width > window.innerWidth - 8
  const left = overflowsRight ? Math.max(8, rect.right - width) : rect.left
  return {
    top: `${rect.bottom + 6}px`,
    left: `${left}px`,
    width: `${width}px`,
  }
}

function toggleOpen() {
  const opening = !open.value
  if (opening) panelStyle.value = positionPanel()
  open.value = opening
  activeIndex.value = -1
}

watch(() => props.categories, () => {
  activeIndex.value = -1
  optionRefs.value = []
})

watch(activeIndex, (index) => {
  if (index >= 0) optionRefs.value[index]?.scrollIntoView({ block: 'nearest' })
})

function handleKeydown(e) {
  if (!open.value) {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      toggleOpen()
    }
    return
  }

  if (e.key === 'ArrowDown') {
    e.preventDefault()
    if (props.categories.length) activeIndex.value = (activeIndex.value + 1) % props.categories.length
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    if (props.categories.length) {
      activeIndex.value = activeIndex.value <= 0 ? props.categories.length - 1 : activeIndex.value - 1
    }
  } else if (e.key === 'Escape') {
    open.value = false
  } else if (e.key === 'Enter' && activeIndex.value >= 0) {
    e.preventDefault()
    emit('toggle', props.categories[activeIndex.value].value)
  }
}
</script>
